import React, { FC, useEffect, useState } from 'react';
import { Payment, PaymentTypeEnum, Transaction } from '../../../Customers/types/types';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import {
  Button,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import Close from '@material-ui/icons/Close';
import { formatMoney, formatMonthDayYear } from '../../../../utils/utils';
import PaymentForm from '../../../DashBoard/ActionItem/PaymentForm';
import Grid from '@material-ui/core/Grid/Grid';
import { useUserDetails } from '../../../../UserContext/UserContex';

const createTableData = (
  transactions: Transaction[],
  action: (id) => void,
  shouldShowAction?: (transaction: Omit<Transaction, 'type'>) => boolean
) => {
  return transactions.map((transaction) => {
    const getAction = () => {
      if (shouldShowAction) {
        return shouldShowAction(transaction) ? <DeleteForeverIcon onClick={() => action(transaction.id)} /> : null;
      } else {
        return (
          <IconButton onClick={() => action(transaction.id)}>
            <DeleteForeverIcon />
          </IconButton>
        );
      }
    };
    return {
      data: {
        bank: transaction.bank,
        amount: formatMoney(transaction.amount),
        type: transaction.type,
        date: formatMonthDayYear(+transaction.date),
        actions: getAction()
      }
    };
  });
};

interface Props {
  payment: Payment;
  title: string;
  subtitle?: string;
  handleDeletePayment: (transactionId: string) => void;
  handleAddPayment: (transaction: Omit<Transaction, 'type'>) => void;
  handleAddNote: (notes) => void;
  shouldShowAction?: (transaction: Omit<Transaction, 'type'>) => boolean;
}

const PaymentDetails: FC<Props> = ({
  payment,
  title,
  subtitle,
  handleAddNote,
  handleDeletePayment,
  handleAddPayment,
  shouldShowAction
}) => {
  const [notes, setNotes] = useState<string>('');
  const [openPaymentForm, setOpenPaymentForm] = useState<boolean>();
  const { permissions } = useUserDetails();
  const canHandlePayments = permissions.canHandlePayments;
  console.log(canHandlePayments);

  useEffect(() => {
    setNotes(payment.notes || '');
  }, []);

  const renderSection = (title: string, type: 'deposit' | 'total') => {
    return (
      <Grid className="space-y-2" item xs={6}>
        <h3 className="text-lg">{title}</h3>
        <div>
          <div className="text-sm text-gray-500">Amount</div>
          <div>{formatMoney(payment[type].amount)}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Remaining</div>
          <div>{formatMoney(payment[type].remaining)}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Deadline</div>
          <div>{formatMonthDayYear(+payment[type].date)}</div>
        </div>
      </Grid>
    );
  };

  const renderSubtitle = () => {
    return subtitle ? ` - ${subtitle}` : '';
  };

  const transactions = createTableData(
    payment.payments.filter((payment) => !!payment.amount),
    handleDeletePayment,
    shouldShowAction
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div className="text-xl font-bold py-2 mb-2">
            {title} {renderSubtitle()}
          </div>
        </div>
      </div>
      <Grid container spacing={3}>
        {renderSection('Deposit', 'deposit')}
        {renderSection('Total', 'total')}
      </Grid>
      <div className="flex justify-between item-center">
        <h3 className="text-lg my-4">Transactions</h3>
        {payment.total.remaining !== 0 && canHandlePayments && (
          <div className="flex items-center">
            <IconButton size="small" color="primary" onClick={() => setOpenPaymentForm((value) => !value)}>
              {openPaymentForm ? <Close fontSize="medium" /> : <AddIcon fontSize="medium" />}
            </IconButton>
          </div>
        )}
      </div>
      <Collapse className="py-2" in={openPaymentForm}>
        <PaymentForm
          remaining={payment.total.remaining}
          onComplete={() => setOpenPaymentForm(false)}
          handleAddPayment={handleAddPayment}
        />
      </Collapse>
      {payment.payments.length > 0 && (
        <div style={{ paddingBottom: '1rem' }}>
          <List className="bg-gray-50 rounded">
            {transactions.map((transaction, index) => (
              <div key={index}>
                <ListItem>
                  <ListItemText
                    primary={transaction.data.amount}
                    secondary={`${transaction.data.date} - ${transaction.data.bank}`}
                  />
                  {canHandlePayments && <ListItemSecondaryAction>{transaction.data.actions}</ListItemSecondaryAction>}
                </ListItem>
                {index < transactions.length - 1 && <Divider style={{ background: 'white' }} component="li" />}
              </div>
            ))}
          </List>
        </div>
      )}
      {payment.payments.length <= 0 && <div style={{ paddingBottom: '1rem' }}>There are no transactions.</div>}
      <div className="my-4">
        <TextField
          id="outlined-multiline-static"
          variant="outlined"
          label="Payment notes"
          style={{ width: '100%' }}
          onChange={(event) => {
            setNotes(event.target.value);
          }}
          multiline
          value={notes}
          defaultValue=""
        />
        <Button style={{ marginTop: '1rem', marginBottom: '1rem' }} variant="contained" onClick={() => handleAddNote(notes)}>
          Save note
        </Button>
      </div>
    </>
  );
};

export default PaymentDetails;
